/**
 * Copyright (C) 2026 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import { Observable, of } from 'rxjs';
import { switchMap, map, catchError } from 'rxjs/operators';
import { ofType, StateObservable } from 'redux-observable';
import { AjaxError, AjaxResponse } from 'rxjs/ajax';
import { Action, ActionCreators } from './actions';
import { RootState } from '../index';
import { handleErrorInterceptor } from '../../utils/AuthUtils';
import {
  normalizeHaloNetGroundStationAvailabilities,
  normalizeHaloNetGroundStationReservations,
  normalizeHaloNetGroundStations,
} from '../../utils/HaloNetUtils';
import { GroundStationAvailabilityDto, GroundStationDataDto, ReservationDto } from '../../shared/entities/haloNet';

const requestHaloNetGroundStationsSuccess = (response: AjaxResponse<any>): Action => {
  const normalizedGroundStations = normalizeHaloNetGroundStations((response.response || []) as GroundStationDataDto[]);
  return ActionCreators.ReceivedHaloNetGroundStationsSuccess.create({ response: normalizedGroundStations });
};

const requestHaloNetGroundStationsError = (error: AjaxError): Action => {
  return ActionCreators.ReceivedHaloNetGroundStationsError.create(error);
};

export const requestHaloNetGroundStationsEpic = (
  action$: Observable<Action>,
  _state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.RequestHaloNetGroundStations.type),
    switchMap(
      (): Observable<Action> =>
        ajaxRaw.get('/halonet/ground-stations').pipe(
          map((response: AjaxResponse<any>): Action => requestHaloNetGroundStationsSuccess(response)),
          catchError(
            (error: AjaxError): Observable<Action> =>
              handleErrorInterceptor(error, of(requestHaloNetGroundStationsError(error))),
          ),
        ),
    ),
  );

const requestHaloNetGroundStationAvailabilitySuccess = (response: AjaxResponse<any>): Action => {
  const normalizedAvailabilities = normalizeHaloNetGroundStationAvailabilities(
    (response.response || []) as GroundStationAvailabilityDto[],
  );

  return ActionCreators.ReceivedHaloNetGroundStationAvailabilitySuccess.create({ response: normalizedAvailabilities });
};

const requestHaloNetGroundStationAvailabilityError = (error: AjaxError): Action => {
  return ActionCreators.ReceivedHaloNetGroundStationAvailabilityError.create(error);
};

export const requestHaloNetGroundStationAvailabilityEpic = (
  action$: Observable<Action>,
  _state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.RequestHaloNetGroundStationAvailability.type),
    switchMap((action: any): Observable<Action> => {
      const params = new URLSearchParams();
      params.set('type', action.payload.type);

      if (action.payload.svName) {
        params.set('svName', action.payload.svName);
      }

      return ajaxRaw.get(`/halonet/ground-stations/availability?${params.toString()}`).pipe(
        map((response: AjaxResponse<any>): Action => requestHaloNetGroundStationAvailabilitySuccess(response)),
        catchError(
          (error: AjaxError): Observable<Action> =>
            handleErrorInterceptor(error, of(requestHaloNetGroundStationAvailabilityError(error))),
        ),
      );
    }),
  );

const requestHaloNetGroundStationReservationsSuccess = (response: AjaxResponse<any>): Action => {
  const normalizedReservations = normalizeHaloNetGroundStationReservations(
    (response.response || []) as ReservationDto[],
  );
  return ActionCreators.ReceivedHaloNetGroundStationReservationsSuccess.create({ response: normalizedReservations });
};

const requestHaloNetGroundStationReservationsError = (error: AjaxError): Action => {
  return ActionCreators.ReceivedHaloNetGroundStationReservationsError.create(error);
};

export const requestHaloNetGroundStationReservationsEpic = (
  action$: Observable<Action>,
  _state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.RequestHaloNetGroundStationReservations.type),
    switchMap(
      (): Observable<Action> =>
        ajaxRaw.get('/halonet/ground-stations/reservations').pipe(
          map((response: AjaxResponse<any>): Action => requestHaloNetGroundStationReservationsSuccess(response)),
          catchError(
            (error: AjaxError): Observable<Action> =>
              handleErrorInterceptor(error, of(requestHaloNetGroundStationReservationsError(error))),
          ),
        ),
    ),
  );
